/**
 * FunnelFlowChart - Nivo-powered Funnel Visualization
 *
 * Smooth curved funnel with gradients, animation, and full React control.
 * Uses @nivo/funnel's ResponsiveFunnel for a polished analytics look.
 */

import { useMemo } from "react";
import { motion } from "framer-motion";
import { ResponsiveFunnel } from "@nivo/funnel";
import { cn } from "@/lib/utils";
import { formatNumeric } from "@/lib/utils/stringUtils";
import { safeRatio } from "@/lib/utils/stats";
import type { FunnelResponse, FunnelStepResult } from "@/lib/types/funnel-api";
import type { FunnelCustomLayerProps, FunnelDatum, FunnelPart } from "@nivo/funnel";

/** Mirrors FunnelStudio's StepType — kept local to avoid coupling the chart to the builder. */
type StepEventType = "page_view" | "click" | "form_submission";

/** Extra fields carried on each nivo datum so layers/tooltip know how to label the count. */
type FunnelStepDatum = FunnelDatum<{ type?: StepEventType; totalEvents?: number }>;

interface FunnelFlowChartProps {
  data: FunnelResponse;
  /** Event type per step, aligned by index with `data.data.steps` — drives the unit label (e.g. "Unique visitors submitted" vs "visitors"). */
  stepTypes?: StepEventType[];
  className?: string;
  height?: number;
  /** Overrides the "visitors" noun (the admin signup funnel counts unique users). */
  unitNoun?: string;
}

/** "Unique visitors submitted" for form steps, the unit noun for everything else. */
const unitLabel = (type: StepEventType | undefined, noun = "visitors") =>
  type === "form_submission" ? "Unique visitors submitted" : noun;

const formatPct = (value: number) => {
  if (!Number.isFinite(value)) return "0%";
  if (value >= 100) return "100%";
  if (value >= 10) return `${Math.round(value)}%`;
  return `${value.toFixed(1)}%`;
};

/**
 * Steps sit on the accent tint; the step after the biggest drop (by rate) is
 * the one orange value (Paper chart system, Oct 2026: funnel steps never walk
 * the chart ramp).
 */
function generateFunnelColors(steps: Array<{ total_sessions: number }>): string[] {
  let key = -1
  let worst = 0
  for (let i = 1; i < steps.length; i++) {
    const prev = steps[i - 1].total_sessions
    if (prev <= 0) continue
    const rate = Math.max(prev - steps[i].total_sessions, 0) / prev
    if (rate > worst) {
      worst = rate
      key = i
    }
  }
  return steps.map((_, i) => (i === key ? "var(--primary)" : "var(--primary-tint-ring)"))
}

/** Custom tooltip matching the app's popover style */
function FunnelTooltip({
  part,
  totalEntered,
}: {
  part: FunnelPart<FunnelStepDatum>;
  totalEntered: number;
}) {
  const pctOfTotal = safeRatio(part.data.value, totalEntered) * 100;
  const isForm = part.data.type === "form_submission";
  const label = isForm
    ? `${formatNumeric(part.data.value)} unique visitors submitted`
    : `${formatNumeric(part.data.value)} unique visitors`;
  return (
    <div className="bg-popover border border-border/30 rounded-lg px-3 py-2 shadow-lg text-sm">
      <div className="font-semibold text-foreground">{part.data.label}</div>
      <div className="text-muted-foreground">{label}</div>
      {isForm && part.data.totalEvents != null && (
        <div className="text-muted-foreground">
          {formatNumeric(part.data.totalEvents)} total submissions
        </div>
      )}
      <div className="text-muted-foreground text-xs">
        {formatPct(pctOfTotal)} of total
      </div>
    </div>
  );
}

/** Background layer: alternating subtle shading + vertical divider lines between steps */
function StepBackgroundLayer({
  parts,
  width,
  height,
}: FunnelCustomLayerProps<FunnelStepDatum>) {
  if (parts.length < 2) return null;

  return (
    <g>
      {parts.map((part, i) => {
        // Calculate the zone boundaries for each step
        const zoneLeft = i === 0 ? 0 : (parts[i - 1].x1 + part.x0) / 2;
        const zoneRight = i === parts.length - 1 ? width : (part.x1 + parts[i + 1].x0) / 2;

        return (
          <g key={`bg-${part.data.id}`}>
            {/* Alternating subtle background */}
            {i % 2 === 1 && (
              <rect
                x={zoneLeft}
                y={0}
                width={zoneRight - zoneLeft}
                height={height}
                fill="var(--muted-foreground)"
                opacity={0.03}
              />
            )}
            {/* Vertical divider line between steps */}
            {i > 0 && (
              <line
                x1={zoneLeft}
                y1={0}
                x2={zoneLeft}
                y2={height}
                stroke="var(--border)"
                strokeWidth={1}
                opacity={0.3}
                strokeDasharray="4 3"
              />
            )}
          </g>
        );
      })}
    </g>
  );
}

/** Custom layer that renders step labels (name + count) above each funnel part */
function StepLabelsLayer({
  parts,
  steps,
  unitNoun,
}: FunnelCustomLayerProps<FunnelStepDatum> & {
  steps: FunnelStepResult[]
  unitNoun?: string
}) {
  const maxSessions = steps[0]?.total_sessions || 1;

  return (
    <g>
      {parts.map((part, i) => {
        const centerX = (part.x0 + part.x1) / 2;
        const topY = Math.min(part.y0, part.y1);
        const conversionFromStart = safeRatio(part.data.value, maxSessions) * 100;
        const isForm = part.data.type === "form_submission";
        const totalEvents = part.data.totalEvents;

        // Step-to-step conversion badge (between this and next)
        const prevSessions = i > 0 ? steps[i - 1].total_sessions : part.data.value;
        const conversionFromPrev =
          i > 0 ? safeRatio(part.data.value, prevSessions) * 100 : 100;

        return (
          <g key={part.data.id}>
            {/* Step name */}
            <text
              x={centerX}
              y={topY - 38}
              textAnchor="middle"
              fill="var(--muted-foreground)"
              fontSize="10.5"
              fontWeight="500"
            >
              {String(part.data.label ?? part.data.id).length > 16
                ? String(part.data.label ?? part.data.id).slice(0, 14) + "…"
                : String(part.data.label ?? part.data.id)}
            </text>

            {/* Session count */}
            <text
              x={centerX}
              y={topY - 23}
              textAnchor="middle"
              fill="var(--foreground)"
              fontSize="14"
              fontWeight="700"
              fontFamily="system-ui, -apple-system, sans-serif"
            >
              {formatNumeric(part.data.value)}
            </text>

            {/* Unit label */}
            <text
              x={centerX}
              y={topY - 12}
              textAnchor="middle"
              fill="var(--muted-foreground)"
              fontSize="8.5"
              opacity="0.5"
            >
              {unitLabel(part.data.type, unitNoun)}
            </text>

            {/* Total submissions (form steps only) */}
            {isForm && totalEvents != null && (
              <text
                x={centerX}
                y={topY - 3}
                textAnchor="middle"
                fill="var(--muted-foreground)"
                fontSize="8.5"
                opacity="0.4"
              >
                {formatNumeric(totalEvents)} total submissions
              </text>
            )}

            {/* Conversion pill badge inside shape */}
            {Math.abs(part.y1 - part.y0) > 26 && (
              <g>
                <rect
                  x={centerX - 22}
                  y={(part.y0 + part.y1) / 2 - 9}
                  width={44}
                  height={18}
                  rx={9}
                  fill="rgba(255,255,255,0.92)"
                  stroke="rgba(255,255,255,0.3)"
                  strokeWidth={0.5}
                />
                <text
                  x={centerX}
                  y={(part.y0 + part.y1) / 2 + 4}
                  textAnchor="middle"
                  fill="rgba(0,0,0,0.65)"
                  fontSize="11"
                  fontWeight="600"
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  {formatPct(conversionFromStart)}
                </text>
              </g>
            )}

            {/* Step-to-step conversion badge between steps */}
            {i > 0 && (
              <text
                x={part.x0 - 2}
                y={Math.max(part.y0, part.y1) + 16}
                textAnchor="middle"
                fontSize="10"
                fontWeight="600"
                fill="var(--muted-foreground)"
                opacity="0.6"
              >
                {formatPct(conversionFromPrev)}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}

export function FunnelFlowChart({
  data,
  stepTypes,
  className,
  height = 280,
  unitNoun = "visitors",
}: FunnelFlowChartProps) {
  const steps = data.data.steps;
  const summary = data.summary;

  const nivoData: FunnelStepDatum[] = useMemo(
    () =>
      steps.map((step, i) => ({
        id: step.step_name,
        value: step.total_sessions,
        label: step.step_name,
        type: stepTypes?.[i],
        totalEvents: step.total_events,
      })),
    [steps, stepTypes]
  );

  const colors = useMemo(() => generateFunnelColors(steps), [steps]);

  // Background layer: alternating shading + dividers
  const bgLayer = useMemo(() => {
    const Layer = (props: FunnelCustomLayerProps<FunnelStepDatum>) => (
      <StepBackgroundLayer {...props} />
    );
    Layer.displayName = "StepBackgroundLayer";
    return Layer;
  }, []);

  // Custom layer that captures `steps` via closure
  const labelsLayer = useMemo(() => {
    const Layer = (props: FunnelCustomLayerProps<FunnelStepDatum>) => (
      <StepLabelsLayer {...props} steps={steps} unitNoun={unitNoun} />
    );
    Layer.displayName = "StepLabelsLayer";
    return Layer;
  }, [steps, unitNoun]);

  // "Completed" tracks reaching the last step — label it by that step's type.
  const completedLabel = unitLabel(stepTypes?.at(-1), unitNoun);

  return (
    <motion.div
      className={cn("space-y-4", className)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Summary metrics row */}
      <div className="flex items-center gap-4 pb-3 border-b border-border/30">
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground">Entered</span>
          <span className="text-lg font-semibold text-foreground tabular-nums leading-tight">{formatNumeric(summary.total_entered)}</span>
          <span className="text-[9px] text-muted-foreground/50">{unitNoun}</span>
        </div>
        <div className="w-px h-10 bg-border/40" />
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground">Completed</span>
          <span className="text-lg font-semibold text-foreground tabular-nums leading-tight">{formatNumeric(summary.total_completed)}</span>
          <span className="text-[9px] text-muted-foreground/50">{completedLabel}</span>
        </div>
        <div className="w-px h-10 bg-border/40" />
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground">Dropped</span>
          <span className="text-lg font-semibold text-primary-strong tabular-nums leading-tight">{formatNumeric(summary.total_entered - summary.total_completed)}</span>
          <span className="text-[9px] text-muted-foreground/50">{unitNoun}</span>
        </div>
        <div className="w-px h-10 bg-border/40" />
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground">Conversion</span>
          <span className="text-lg font-semibold text-primary tabular-nums leading-tight">{formatPct(summary.overall_conversion_rate * 100)}</span>
          <span className="text-[9px] text-muted-foreground/50">of entered</span>
        </div>
      </div>

      {/* Nivo Funnel */}
      <div style={{ height }}>
        <ResponsiveFunnel
          data={nivoData}
          direction="horizontal"
          margin={{ top: 58, right: 20, bottom: 20, left: 20 }}
          shapeBlending={0.62}
          interpolation="smooth"
          spacing={3}
          colors={colors}
          fillOpacity={1}
          borderWidth={0}
          enableLabel={false}
          enableBeforeSeparators={false}
          enableAfterSeparators={false}
          currentPartSizeExtension={6}
          currentBorderWidth={0}
          motionConfig="gentle"
          layers={[bgLayer, "parts", labelsLayer]}
          tooltip={({ part }) => (
            <FunnelTooltip
              part={part}
              totalEntered={summary.total_entered}
            />
          )}
          theme={{
            tooltip: {
              container: {
                background: "transparent",
                boxShadow: "none",
                padding: 0,
              },
            },
          }}
        />
      </div>
    </motion.div>
  );
}

export default FunnelFlowChart;
